import type { ILanguageJson } from '../types/languageJson';
import { getRuntimeLocale } from './localeText';
import {
  getSessionRuntimeLocale,
  normalizeRuntimeLocale,
  RUNTIME_LANG_QUERY_KEY,
} from './runtimeLocale';
import {
  LANGUAGE_JSON_KEYS,
  normalizeLanguageJson,
  resolveLanguageJsonText,
} from './languageJson';

const hasText = (value?: string | null): boolean => {
  return typeof value === 'string' && value.trim().length > 0;
};

const ORIGINAL_SHARE_TITLE_PREFIX_REGEXP = /^【(无水印|no watermark|original)】\s*/i;

const getSafePathname = (): string => {
  try {
    return window.location?.pathname || '';
  } catch (error) {
    return '';
  }
};

const normalizePathname = (pathname: string = ''): string => {
  const normalized = String(pathname || '').trim();
  if (!normalized) {
    return '';
  }

  return normalized.replace(/\/+$/, '');
};

const isShareableGalleryPath = (pathname: string = ''): boolean => {
  const normalizedPath = normalizePathname(pathname);

  return /^(\/((gti)|(g))\/[^/]+|\/page\/exhibitor\/[^/]+)$/.test(
    normalizedPath
  );
};

const getCurrentCustomWatermarkId = (): string => {
  const customWatermarkFromParams = window.match_params?.customwatermark;
  if (customWatermarkFromParams) {
    return String(customWatermarkFromParams);
  }

  const pathname = getSafePathname();
  const matched = pathname.match(/\/watermark\/([^/?#]+)/);
  return matched && matched[1] ? decodeURIComponent(matched[1]) : '';
};

const getGlobalLanguageJson = (): ILanguageJson => {
  return normalizeLanguageJson(window.configDetail?.language_json);
};

const getPageLanguageJson = (): ILanguageJson => {
  return normalizeLanguageJson(window.pageDetail?.language_json);
};

/**
 * 分享链路里的语言需要尽量“跟着当前用户正在看的语言走”：
 * - 只有url指定的时候, 才进行设置. 其他情况维持原样
 */
export const appendShareLangToUrl = (url: string = ''): string => {
  if (!url) {
    return '';
  }

  // 因为session中存储的lang是通过url上转化过去的, 所以如果定向指定过, 才在分享的时候添加, 否则不用添加
  const sessionLocale = getSessionRuntimeLocale();
  if (!sessionLocale) {
    return url;
  }

  try {
    if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) {
      const target = new URL(url);
      target.searchParams.set(RUNTIME_LANG_QUERY_KEY, sessionLocale);
      return target.toString();
    }

    const [withoutHash, hash = ''] = url.split('#');
    const [pathname, query = ''] = withoutHash.split('?');
    const params = new URLSearchParams(query);
    params.set(RUNTIME_LANG_QUERY_KEY, sessionLocale);
    const nextQuery = params.toString();
    return `${pathname}${nextQuery ? `?${nextQuery}` : ''}${hash ? `#${hash}` : ''}`;
  } catch (error) {
    return url;
  }
};

export const isOriginalShareContext = (): boolean => {
  const pathname = getSafePathname();

  if (/\/original\/[^/?#]+/.test(pathname)) {
    return true;
  }

  if (window.match_params && Number(window.match_params.val) === 1) {
    return true;
  }

  return Boolean(window.original_photo);
};

export const getCurrentShareRouteParams = (): string => {
  if (isOriginalShareContext()) {
    return '/original/1';
  }

  const customWatermarkId = getCurrentCustomWatermarkId();
  if (customWatermarkId) {
    return `/watermark/${customWatermarkId}`;
  }

  return '';
};

export const appendCurrentShareRouteParams = (url: string = ''): string => {
  if (!url) {
    return '';
  }

  const routeParams = getCurrentShareRouteParams();
  if (!routeParams) {
    return url;
  }

  try {
    if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) {
      const target = new URL(url);
      if (
        !isShareableGalleryPath(target.pathname) ||
        /\/(original|watermark)\//.test(target.pathname)
      ) {
        return url;
      }

      target.pathname = `${normalizePathname(target.pathname)}${routeParams}`;
      return target.toString();
    }

    const [withoutHash, hash = ''] = url.split('#');
    const [pathname, query = ''] = withoutHash.split('?');
    const normalizedPath = normalizePathname(pathname);

    if (
      !isShareableGalleryPath(normalizedPath) ||
      /\/(original|watermark)\//.test(normalizedPath)
    ) {
      return url;
    }

    return `${normalizedPath}${routeParams}${query ? `?${query}` : ''}${
      hash ? `#${hash}` : ''
    }`;
  } catch (error) {
    return url;
  }
};

export const decorateShareTitleForCurrentContext = (
  title: string = '',
  locale: string = getRuntimeLocale()
): string => {
  const finalTitle = String(title || '').trim();
  if (!finalTitle || !isOriginalShareContext()) {
    return finalTitle;
  }

  if (ORIGINAL_SHARE_TITLE_PREFIX_REGEXP.test(finalTitle)) {
    return finalTitle;
  }

  const normalizedLocale = normalizeRuntimeLocale(locale) || 'zh-CN';
  const prefix =
    normalizedLocale === 'en-US' ? '【no watermark】' : '【无水印】';

  return `${prefix}${finalTitle}`;
};

export const getLocalizedGalleryPrefix = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.gallery?.prefix,
    getGlobalLanguageJson(),
    LANGUAGE_JSON_KEYS.prefix,
    locale
  );
};

export const getLocalizedPageNavigator = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.pageDetail?.navigator,
    getPageLanguageJson(),
    LANGUAGE_JSON_KEYS.navigator,
    locale,
    window.pageDetail?.navigator_i18n
  );
};

export const getLocalizedGlobalShareTitle = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.configDetail?.h5_share_title,
    getGlobalLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareTitle,
    locale
  );
};

export const getLocalizedGlobalShareText = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.configDetail?.h5_share_text,
    getGlobalLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareText,
    locale
  );
};

export const getLocalizedPageShareTitle = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.pageDetail?.h5_share_title,
    getPageLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareTitle,
    locale
  );
};

export const getLocalizedPageShareText = (locale: string = getRuntimeLocale()): string => {
  return resolveLanguageJsonText(
    window.pageDetail?.h5_share_text,
    getPageLanguageJson(),
    LANGUAGE_JSON_KEYS.h5ShareText,
    locale
  );
};

export const hasPageShareOverride = (locale: string = getRuntimeLocale()): boolean => {
  return Boolean(
    window.pageDetail?.h5_share_image &&
      hasText(getLocalizedPageShareTitle(locale)) &&
      hasText(getLocalizedPageShareText(locale))
  );
};

export const getEffectiveShareTitle = (locale: string = getRuntimeLocale()): string => {
  if (hasPageShareOverride(locale)) {
    return getLocalizedPageShareTitle(locale);
  }

  if (
    window.dataDetail &&
    window.dataDetail.name &&
    window.dataDetail.id !== window.gallery.id
  ) {
    return `${getLocalizedGalleryPrefix(locale)}${window.dataDetail.name}${
      window.gallery?.name ? `-${window.gallery.name}` : ''
    }`;
  }

  const globalTitle = getLocalizedGlobalShareTitle(locale);
  if (hasText(globalTitle)) {
    return `${getLocalizedGalleryPrefix(locale)}${globalTitle}`;
  }

  return `${getLocalizedGalleryPrefix(locale)}${window.gallery?.name || ''}`;
};

export const getEffectiveShareText = (locale: string = getRuntimeLocale()): string => {
  if (hasPageShareOverride(locale)) {
    return getLocalizedPageShareText(locale);
  }

  return getLocalizedGlobalShareText(locale);
};

export const getEffectiveShareImageKey = (): string => {
  if (hasPageShareOverride()) {
    return window.pageDetail?.h5_share_image || '';
  }

  return window.configDetail?.h5_share_image || '';
};
